// client/src/components/BidEstimation/VendorQuotes/ProductDetailSidebar.js
import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@material-ui/core'
import axios from 'axios'

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

const fmt = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '\u2014'

const SOURCE_LABELS = {
  catalog_extension: 'Internal Catalog',
  vendor_catalog: 'Vendor Catalog',
  quote_only: 'Quote Data Only'
}

const MATCH_SOURCE_LABELS = {
  sku_cache: 'SKU Cache',
  aspire: 'Aspire Catalog',
  vendor_catalog: 'Vendor Catalog',
  unmatched: 'Unmatched'
}

const COVERAGE_COLORS = {
  covered: { bg: 'var(--ok-tint)', color: 'var(--ok)', label: 'Covered' },
  partial: { bg: 'var(--warn-tint)', color: 'var(--warn)', label: 'Partial' },
  missing: { bg: 'var(--risk-tint)', color: 'var(--risk)', label: 'Missing' }
}

const titleCase = value => String(value || '')
  .replace(/[_-]/g, ' ')
  .replace(/\b\w/g, c => c.toUpperCase())

const formatCatalogPage = vendorCatalog => {
  if (!vendorCatalog) return null
  const page = vendorCatalog.pageLabel || vendorCatalog.pageNumber
  return page ? `Page ${page}` : null
}

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
      color: 'var(--ink-4)', marginBottom: 8, paddingBottom: 4,
      borderBottom: '1px solid var(--line-1)'
    }}>
      {title}
    </div>
    {children}
  </div>
)

const Field = ({ label, value, mono }) => {
  if (!value && value !== 0) return null
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-4)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, color: 'var(--ink)',
        fontFamily: mono ? 'var(--f-mono, var(--font-mono))' : 'inherit',
        wordBreak: 'break-word'
      }}>
        {value}
      </div>
    </div>
  )
}

const AttributeList = ({ attributes }) => {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return null
  const entries = Object.entries(attributes).filter(([, value]) => value != null && value !== '')
  if (entries.length === 0) return null

  return (
    <div style={{
      background: 'var(--bg-panel-alt)', borderRadius: 6, padding: '8px 12px',
      fontSize: 12
    }}>
      {entries.map(([key, value], i) => (
        <div key={key} style={{
          display: 'flex', justifyContent: 'space-between', gap: 12,
          padding: '4px 0',
          borderBottom: i < entries.length - 1 ? '1px solid var(--line-1)' : 'none'
        }}>
          <span style={{ color: 'var(--ink-3)', flexShrink: 0 }}>{titleCase(key)}</span>
          <span style={{
            fontFamily: 'var(--f-mono, var(--font-mono))',
            color: 'var(--ink)', textAlign: 'right', wordBreak: 'break-word'
          }}>
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </span>
        </div>
      ))}
    </div>
  )
}

const PriceCard = ({ label, price, highlight }) => (
  <div style={{
    flex: 1, padding: '10px 12px', borderRadius: 8,
    background: highlight ? 'var(--ok-tint)' : 'var(--bg-panel-alt)',
    border: highlight ? '1px solid var(--ok)' : '1px solid var(--line-1)',
    textAlign: 'center', minWidth: 0
  }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 4 }}>{label}</div>
    <div style={{
      fontSize: 16, fontWeight: 700,
      fontFamily: 'var(--f-mono, var(--font-mono))',
      color: highlight ? 'var(--ok-deep, var(--ok))' : 'var(--ink)'
    }}>
      {fmt(price)}
    </div>
  </div>
)

export default function ProductDetailSidebar ({ quoteId, lineItemId, open, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !quoteId || !lineItemId) return
    setLoading(true)
    setError(null)
    setData(null)
    axios.get(`/api/bid-estimation/vendor-quotes/${quoteId}/line-items/${lineItemId}/product-detail`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false))
  }, [open, quoteId, lineItemId])

  if (!open) return null

  const coverageInfo = data?.coverageStatus ? COVERAGE_COLORS[data.coverageStatus] : null
  const hasAccountPrice = data?.pricing?.vendorAccountPrice != null
  const hasDistinctRegularPrice = data?.pricing?.vendorRegularPrice != null && data.pricing.vendorRegularPrice !== data.pricing.vendorAccountPrice
  const hasCatalogAttributes = data?.attributesRaw &&
    typeof data.attributesRaw === 'object' &&
    !Array.isArray(data.attributesRaw) &&
    Object.values(data.attributesRaw).some(value => value != null && value !== '')

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 1300,
          background: 'rgba(0,0,0,0.15)', transition: 'opacity 0.2s'
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1301,
        width: 420, maxWidth: '100vw',
        background: 'var(--bg-panel)', borderLeft: '1px solid var(--line-2)',
        boxShadow: '-8px 0 24px rgba(30,40,35,0.12)',
        overflow: 'auto', display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.2s ease-out'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 20px 0 20px', position: 'sticky', top: 0,
          background: 'var(--bg-panel)', zIndex: 1
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {loading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading...</div>}
            {data && (
              <>
                <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, marginBottom: 4 }}>
                  {data.productName || 'Unknown Product'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{
                    fontSize: 11, fontFamily: 'var(--f-mono, var(--font-mono))',
                    color: 'var(--ink-3)', background: 'var(--bg-panel-alt)',
                    padding: '1px 6px', borderRadius: 4
                  }}>
                    {data.sku || '\u2014'}
                  </span>
                  {data.source && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: data.source === 'catalog_extension' ? 'var(--ok-tint)' : 'var(--info-tint)',
                      color: data.source === 'catalog_extension' ? 'var(--ok)' : 'var(--info)'
                    }}>
                      {SOURCE_LABELS[data.source] || data.source}
                    </span>
                  )}
                  {data.hasVendorCatalogDetails && data.source !== 'vendor_catalog' && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: 'var(--info-tint)', color: 'var(--info)'
                    }}>
                      Vendor Details
                    </span>
                  )}
                  {coverageInfo && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 4,
                      background: coverageInfo.bg, color: coverageInfo.color
                    }}>
                      {coverageInfo.label}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 4, color: 'var(--ink-3)',
              marginLeft: 8, flexShrink: 0
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 20px 20px', flex: 1 }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress size={24} />
            </div>
          )}

          {error && (
            <div style={{ padding: 16, color: 'var(--risk)', fontSize: 13 }}>
              {error}
            </div>
          )}

          {data && (
            <>
              {/* Product image */}
              {data.photo && (
                <div style={{
                  marginBottom: 16, borderRadius: 8, overflow: 'hidden',
                  background: '#f8f8f8', display: 'flex', justifyContent: 'center',
                  padding: 12, border: '1px solid var(--line-1)'
                }}>
                  <img
                    src={data.photo}
                    alt={data.productName || 'Product'}
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* Pricing comparison */}
              <Section title="Pricing">
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {data.pricing.ourPrice != null && (
                    <PriceCard label="Our Price" price={data.pricing.ourPrice} highlight />
                  )}
                  {hasAccountPrice && (
                    <PriceCard label="Account" price={data.pricing.vendorAccountPrice} />
                  )}
                  {hasDistinctRegularPrice && (
                    <PriceCard label="List" price={data.pricing.vendorRegularPrice} />
                  )}
                  {!hasAccountPrice && data.pricing.vendorPrice != null && (
                    <PriceCard label="Vendor" price={data.pricing.vendorPrice} />
                  )}
                  {data.pricing.quotePrice != null && (
                    <PriceCard label="Quote Price" price={data.pricing.quotePrice} />
                  )}
                  {data.pricing.ourPrice == null && data.pricing.vendorPrice == null && data.pricing.quotePrice == null && (
                    <PriceCard label="Quote Price" price={data.pricing.quotePrice} />
                  )}
                </div>
                {data.pricing.quoteQty != null && (
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {data.pricing.quoteQty} {data.pricing.quoteUom || ''} @ {fmt(data.pricing.quotePrice)} = <b style={{ color: 'var(--ink)' }}>{fmt(data.pricing.quoteExtension)}</b>
                  </div>
                )}
              </Section>

              {/* Identity / Specs */}
              <Section title="Product Details">
                <Field label="Vendor" value={data.vendorName || data.vendorCatalog?.vendorName} />
                <Field label="Vendor SKU" value={data.vendorCatalog?.vendorItemNumber || data.sku} mono />
                <Field label="Manufacturer" value={data.manufacturer} />
                <Field label="MPN" value={data.mpn} mono />
                <Field label="Category" value={data.category} />
                {data.subcategory && <Field label="Subcategory" value={data.subcategory} />}
                {data.shortDescription && <Field label="Description" value={data.shortDescription} />}
              </Section>

              {data.vendorCatalog && (
                <Section title="Vendor Catalog">
                  <Field label="Catalog Version" value={data.vendorCatalog.catalogVersion} />
                  <Field label="Catalog Source" value={titleCase(data.vendorCatalog.sourceType)} />
                  <Field label="Catalog Page" value={formatCatalogPage(data.vendorCatalog)} />
                  <Field label="External ID" value={data.vendorCatalog.externalId} mono />
                  <Field label="Extraction Status" value={titleCase(data.vendorCatalog.extractionStatus)} />
                  {data.vendorCatalog.confidenceScore != null && (
                    <Field label="Catalog Confidence" value={`${Math.round(data.vendorCatalog.confidenceScore * 100)}%`} />
                  )}
                </Section>
              )}

              {/* Specifications */}
              {data.specs && data.specs.length > 0 && (
                <Section title="Specifications">
                  <div style={{
                    background: 'var(--bg-panel-alt)', borderRadius: 6, padding: '8px 12px',
                    fontSize: 12
                  }}>
                    {data.specs.map((s, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between',
                        padding: '4px 0',
                        borderBottom: i < data.specs.length - 1 ? '1px solid var(--line-1)' : 'none'
                      }}>
                        <span style={{ color: 'var(--ink-3)' }}>{s.label}</span>
                        <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', color: 'var(--ink)' }}>{s.value}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {hasCatalogAttributes && (
                <Section title="Catalog Attributes">
                  <AttributeList attributes={data.attributesRaw} />
                </Section>
              )}

              {data.rawSpecText && (
                <Section title="Catalog Notes">
                  <div style={{
                    background: 'var(--bg-panel-alt)', borderRadius: 6, padding: '10px 12px',
                    border: '1px solid var(--line-1)', fontSize: 12, lineHeight: 1.45,
                    color: 'var(--ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {data.rawSpecText}
                  </div>
                </Section>
              )}

              {/* Match Info */}
              <Section title="Match Info">
                <Field label="Matched Via" value={MATCH_SOURCE_LABELS[data.matchInfo.matchSource] || data.matchInfo.matchSource} />
                {data.matchInfo.matchScore != null && (
                  <Field label="Confidence" value={`${Math.round(data.matchInfo.matchScore * 100)}%`} />
                )}
                <Field label="Status" value={data.matchInfo.mappingStatus} />
              </Section>

              {/* External link */}
              {data.externalUrl && (
                <a
                  href={data.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 8,
                    border: '1px solid var(--line-2)', background: 'var(--bg-panel)',
                    color: 'var(--brand-deep, var(--brand))', fontSize: 13, fontWeight: 550,
                    textDecoration: 'none', cursor: 'pointer',
                    transition: 'background 0.12s'
                  }}
                >
                  View on Vendor Site <ExternalLinkIcon />
                </a>
              )}

              {/* Future: Alternates placeholder */}
              {data.source === 'catalog_extension' && (
                <div style={{
                  marginTop: 20, padding: '12px 14px', borderRadius: 8,
                  background: 'var(--bg-panel-alt)', border: '1px dashed var(--line-2)',
                  fontSize: 12, color: 'var(--ink-4)', textAlign: 'center'
                }}>
                  Alternate options coming soon
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}
